import React, { useEffect } from 'react';
import styled from 'styled-components';
import ColorPicker from '../../right-panel/settings/color-picker';
import SelectSmall from '../select-small';
import Icon, { COLORS, ICONS } from '../icon';
import DurationPicker from '../duration-picker';

const MAX_DURATION = (60 * 10) - 1;

const disabledCss = `
	border: none;
	background-color: #312F38;
	opacity: 1;
	color: #908CA0;
`;

const StyledInput = styled.input`
	&.input {
		background-color: #1a1a1e;
		border: 1px solid #908CA0;
		display: inline-block;
		border-radius: 20px;
		box-sizing: border-box;
		color: #E5E4EC;
		font-family: 'Poppins', sans-serif;
		font-style: normal;
		font-size: 12px;
		font-weight: 500;
		height: 48px;
		line-height: 16px;
		width: 100%;
		margin: 0;
		background: transparent;
		border-radius: 100px;
		&:focus {
			border: 1px solid #E5E4EC;
			outline: none;
		}
		&:hover {
			background-color: #312F36;
		}
		&.disabled {
			${disabledCss}
		}
	}
`;

export const StyledInputContainer = styled.div`
	&.input-container {
		margin-top: 12px;
		margin-bottom: 0;
		& > label {
			font-size: 11px;
			font-weight: 600;
			margin-left: 16px;
			margin-right: 10px;
			color: #BFBBCF;
		}
		&:last-child {
			margin-bottom: 10px;
		}
		& > .color-toggle {
			border-color: #908CA0;
			border-radius: 100px;
			height: 40px;
			width: 120px;
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			& > .color-swatch {
				margin-left: 4px;
				height: 32px;
				width: 32px;
			}
			& > span {
				font: 'Poppins', sans-serif;
				text-overflow: initial;
				font-size: 12px;
				font-weight: 500;
			}
			&.disabled {
				${disabledCss}
			}
		}
	}
`;

export const StyledInputText = styled(StyledInputContainer)`
	position: relative;
	> .lock {
		position: absolute;
		right: 16px;
		top: calc(100% - 16px);
		z-index:1;
		display: none;
	}

	&:nth-child(n) {
		margin: 0 0 4px -8px;
		& > input {
			width: calc(100% + 8px);
			height: initial;
			border-radius: 4px;
			border: 1px solid #26242B;
			padding: 8px;
			&:focus {
				border: 1px solid #908CA0;
				outline: none;
			}
		}
		&.disabled {
			${disabledCss}
			margin-bottom:0;
			border-radius: 12px;
		}
	}
	&:nth-child(2) {
		& > input {
			font-size: 30px;
			font-weight: 700;
			padding: 6px 8px;
			&.disabled {
				border-radius: 12px 12px 0 0;
				padding: 16px 16px 8px 16px;
			}
		}
		&.disabled {
			> .lock {
				display: block;
			}
		}
	}
	&:nth-child(3) {
		& > input {
			font-size: 20px;
			font-weight: 600;
			padding: 6px 8px;
			&.disabled {
				border-radius: 0 0 12px 12px;
				padding: 8px 16px 16px 16px;
			}
		}
	}
	&:nth-child(n+4) {
		& > input {
			font-size: 14px;
			font-weight: 600;
			padding: 8px;
		}
	}
	& > label {
		visibility: hidden;
	}
`;

export const StyledInputRow = styled(StyledInputContainer)`
	display: flex;
	flex-direction: row !important;
	margin-top: 3px;
	margin-left: 0;
	justify-content: space-between !important;
	font-size: 12px;
	&.input-container {
		position: relative;
		> .lock {
			position: absolute;
			right: 14px;
			top: calc(50% + 12px);
			transform: translateY(-50%);
		}
		&.style-row {
			margin-top: 15px
		}
		& > label {
			margin-top: 13px;
			margin-left: 0;
			margin-right: 0;
			font-size: 12px;
			font-weight: 500;
		}
		> .color-picker-wrapper > .open {
			bottom: 0;
		}
	}
	& > .select-small.title {
		opacity: 1 !important;
		margin-bottom: 0;
		font-weight: 500;
		& > span.select-label.dropdown {
			font-size: 12px;
			font-weight: 500;
		}

		&.disabled {
			& > span.select-label.dropdown {
				background-color: #312F38;
				border: none;
				opacity: 1;
				color: #908CA0 !important;
			}
		}
	}
  
  	.duration-container {
	  margin-bottom: 12px;
    }
`;

interface GRInputProps {
	key: string;
	onChange: (event: any) => void;
	value: string;
	fieldType?: string;
	required?: boolean;
	inputStyle?: string;
	labelStyle?: string;
	onBlur?: () => void;
	onFocus?: () => void;
	autoFocus?: boolean;
	noAutocomplete?: boolean;
	disabled?: boolean;
	customClassNameProp?: string;
	friendlyName: string;
	fieldName: string;
	options: any;
}

export default function GRInput(props: GRInputProps) {
	const {
		onChange,
		value,
		fieldType = 'text',
		required = false,
		inputStyle = '',
		labelStyle = '',
		onBlur = () => {},
		onFocus = () => {},
		autoFocus = false,
		noAutocomplete = true,
		disabled = false,
		customClassNameProp = '',
		friendlyName,
		fieldName,
		options,
	} = props;

	const [isOpen, setIsOpen] = React.useState(false);

	useEffect(() => {
		setIsOpen(false);
	}, []);

	const [localValue, setLocalValue] = React.useState(value);
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	if (fieldType === 'select') {
		// convert options to consumable format for SelectSmall
		const selectOptions = options.map((option: any) => ({
			label: option.name,
			value: option.value,
			className: option.className,
		}));
		const currentOption = selectOptions.find((option: any) => option.value === localValue);

		// eslint-disable-next-line @typescript-eslint/no-shadow
		const onChangeWrapper = (value: any) => {
			setLocalValue(value ?? '');
			onChange({
				target: {
					name: fieldName,
					value: value ?? '',
				},
			});
		};
		if (!currentOption) {
			return null;
		}
		return (
			<StyledInputRow className="option-row input-container style-row">
				<SelectSmall {...props}
					dropdown
					selected={currentOption}
					options={selectOptions}
					label={friendlyName}
					onChange={onChangeWrapper}
					showLabel
					onBlur={onBlur}
					onFocus={onFocus}
					disabled={disabled}
				/>
				{disabled && <div className='lock'><Icon name={ICONS.LOCK} color={COLORS.TEXT_GRAY_400} size={16} /></div>}
			</StyledInputRow>

		);
	} if (fieldType === 'color') {
		const onChangeWrapper = (event: any) => {
			setLocalValue(event.hex);
			onChange({
				target: {
					name: fieldName,
					value: event.hex ?? '',
					...event.target,
				},
			});
		};

		const openHandler = () => {
			setIsOpen((prev) => {
				!prev ? onFocus() : onBlur();
				return !prev;
			});
		};

		return (
			<StyledInputRow
				key={fieldName}
				className={`input-container style-row ${customClassNameProp}`}
			>
				<label className={labelStyle} htmlFor={fieldName}>{friendlyName}</label>
				<div className={`color-toggle ${disabled ? 'disabled' : ''}`} onClick={openHandler}>
					<div className="color-swatch" style={{ backgroundColor: localValue }}></div>
					<span className='format-hex'>{localValue}</span>
				</div>
				<ColorPicker
					value={localValue ?? '#000000'}
					color={localValue}
					label={friendlyName}
					open={isOpen}
					close={() => {
						setIsOpen(false);
						onBlur();
					}}
					onChange={onChangeWrapper}
				/>
			</StyledInputRow>
		);
	} if (fieldType === 'timer') {
		return (
			<StyledInputRow
				key={fieldName}
				className={`input-container style-row ${customClassNameProp}`}
			>
				<label className={labelStyle} htmlFor={fieldName}>{friendlyName}</label>
				<DurationPicker
					initialDuration={Number(localValue ?? 60 * 5)}
					getDurationCB={(e: any) => {
						let newValue = Number(e);

						if (newValue > MAX_DURATION) {
							newValue = MAX_DURATION;
						}
						setLocalValue(newValue.toString());
						onChange({
							target: {
								name: fieldName,
								value: newValue?.toString() ?? '',
							},
						});
					}}
				/>
			</StyledInputRow>
		);
	}

	return (
        <StyledInputText
			key={fieldName}
			className={`input-container ${customClassNameProp} ${disabled ? 'disabled' : ''}`}
			onBlur={onBlur}
			onFocus={onFocus}
		>
            <StyledInput
                id={fieldName}
                name={fieldName}
                type={fieldType}
                autoFocus={autoFocus}
                autoComplete={noAutocomplete ? 'off' : 'on'}
                disabled={disabled}
				className={`input ${inputStyle} ${disabled ? 'disabled' : ''}`}
                placeholder={`${friendlyName}`}
				value={localValue ?? ''}
                onChange={(e) => { setLocalValue(e.target.value); onChange(e); }}
                required={required}
            />

			{disabled && <div className='lock'><Icon name={ICONS.LOCK} color={COLORS.TEXT_GRAY_400} size={16} /></div>}
        </StyledInputText>
	);
}
